import { apiRequest } from '../../../shared/api';
import {
  SafetyObjective,
  IndicatorAnnualSummary,
  SafetyIndicator,
  SafetyPerformanceDashboardStats,
  SafetyPerformanceReview,
  PerformanceSnapshot,
  IndicatorValue,
  IndicatorTarget,
  IndicatorThreshold,
  ObjectiveCategory,
  ObjectiveStatus,
  IndicatorType,
  IndicatorStatus,
} from './types';

export const performanceService = {
  // Dashboard stats
  getDashboardStats: (organizationId?: string, year?: number) => {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (year) params.append('year', year.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiRequest<SafetyPerformanceDashboardStats>(`/performance/analytics/dashboard${query}`);
  },

  // Objectives
  getObjectives: (params: {
    organizationId?: string;
    category?: ObjectiveCategory;
    department?: string;
    status?: ObjectiveStatus;
  } = {}) => {
    const query = new URLSearchParams();
    if (params.organizationId) query.append('organizationId', params.organizationId);
    if (params.category) query.append('category', params.category);
    if (params.department) query.append('department', params.department);
    if (params.status) query.append('status', params.status);
    const qs = query.toString();
    return apiRequest<SafetyObjective[]>(`/performance/objectives${qs ? `?${qs}` : ''}`);
  },

  createObjective: (data: {
    organizationId?: string;
    title: string;
    description: string;
    category: ObjectiveCategory;
    department: string;
    targetDate: string;
    createdBy: string;
  }) => {
    return apiRequest<SafetyObjective>('/performance/objectives', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Indicators
  getIndicators: (params: {
    organizationId?: string;
    objectiveId?: string;
    indicatorType?: IndicatorType;
    status?: IndicatorStatus;
    period?: string;
    year?: number;
    department?: string;
    ownerDepartment?: string;
    dataProviderDepartment?: string;
    searchTerm?: string;
  } = {}) => {
    const query = new URLSearchParams();
    if (params.organizationId) query.append('organizationId', params.organizationId);
    if (params.objectiveId) query.append('objectiveId', params.objectiveId);
    if (params.indicatorType) query.append('indicatorType', params.indicatorType);
    if (params.status) query.append('status', params.status);
    if (params.period) query.append('period', params.period);
    if (params.year) query.append('year', params.year.toString());
    if (params.department) query.append('department', params.department);
    if (params.ownerDepartment) query.append('ownerDepartment', params.ownerDepartment);
    if (params.dataProviderDepartment) query.append('dataProviderDepartment', params.dataProviderDepartment);
    if (params.searchTerm) query.append('searchTerm', params.searchTerm);
    const qs = query.toString();
    return apiRequest<SafetyIndicator[]>(`/performance/indicators${qs ? `?${qs}` : ''}`);
  },

    getIndicatorAnnualSummaries: (indicatorId: string) => {
    return apiRequest<IndicatorAnnualSummary[]>('/performance/indicators/' + indicatorId + '/annual-summary');
  },

  getIndicatorById: (id: string, year?: number) => {
    const query = year ? `?year=${year}` : '';
    return apiRequest<SafetyIndicator>(`/performance/indicators/${id}${query}`);
  },

  createIndicator: (data: {
    organizationId?: string;
    objectiveId: string;
    name: string;
    description: string;
    indicatorType: IndicatorType;
    frequency?: string;
    unit?: string;
    department: string;
    ownerDepartment?: string;
    ownerUserId?: string;
    ownerName?: string;
    ownerRole?: string;
    dataProviderDepartment?: string;
    dataProviderUserId?: string;
    dataProviderName?: string;
    dataProviderRole?: string;
    dataProviderInstructions?: string;
    stakeholderDepartments?: string[];
    thresholdMethodology?: string;
    targetAmberValue?: number;
    targetRedValue?: number;
    createdBy: string;
    formula?: {
      formulaExpression: string;
      numeratorDescription: string;
      denominatorDescription: string;
      multiplier?: number;
    };
  }) => {
    return apiRequest<SafetyIndicator>('/performance/indicators', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateIndicatorRoles: (id: string, data: {
    ownerDepartment?: string;
    ownerUserId?: string;
    ownerName?: string;
    ownerRole?: string;
    dataProviderDepartment?: string;
    dataProviderUserId?: string;
    dataProviderName?: string;
    dataProviderRole?: string;
    dataProviderInstructions?: string;
    frequency?: string;
    stakeholderDepartments?: string[];
    thresholdMethodology?: string;
    targetAmberValue?: number;
    targetRedValue?: number;
  }) => {
    return apiRequest<SafetyIndicator>(`/performance/indicators/${id}/roles`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  archiveIndicator: (id: string) => {
    return apiRequest<SafetyIndicator>(`/performance/indicators/${id}/archive`, {
      method: 'POST',
    });
  },

  unarchiveIndicator: (id: string) => {
    return apiRequest<SafetyIndicator>(`/performance/indicators/${id}/unarchive`, {
      method: 'POST',
    });
  },

  getMissingIndicatorAlerts: (year?: number) => {
    const query = year ? `?year=${year}` : '';
    return apiRequest<any>(`/performance/missing-alerts${query}`);
  },

  getActiveThresholdAlerts: (year?: number) => {
    const query = year ? `?year=${year}` : '';
    return apiRequest<any>(`/performance/threshold-alerts${query}`);
  },

  getSpiStatistics: (year?: number) => {
    const query = year ? `?year=${year}` : '';
    return apiRequest<any>(`/performance/statistics${query}`);
  },

  getDepartmentSpiAuthorizedUsers: (dept: string) => {
    return apiRequest<any[]>(`/performance/departments/${encodeURIComponent(dept)}/spi-permissions`);
  },

  grantDepartmentSpiRole: (dept: string, payload: { userId: string; userName?: string; roleTitle?: string }) => {
    return apiRequest<any>(`/performance/departments/${encodeURIComponent(dept)}/spi-permissions`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  revokeDepartmentSpiRole: (dept: string, userId: string) => {
    return apiRequest<any>(`/performance/departments/${encodeURIComponent(dept)}/spi-permissions/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    });
  },

  // Record monthly value
  recordIndicatorValue: (indicatorId: string, data: {
    period: string;
    numerator?: number;
    numeratorValue?: number;
    denominator?: number;
    denominatorValue?: number;
    recordedBy?: string;
    notes?: string;
    status?: string;
  }) => {
    const num = data.numerator ?? data.numeratorValue ?? 0;
    const den = data.denominator ?? data.denominatorValue ?? 1;
    return apiRequest<IndicatorValue>(`/performance/indicators/${indicatorId}/values`, {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        numerator: num,
        numeratorValue: num,
        denominator: den,
        denominatorValue: den,
      }),
    });
  },

  // Batch Record Monthly Values (Excel grid)
  recordIndicatorValuesBatch: (indicatorId: string, items: Array<{
    period: string;
    numerator?: number;
    numeratorValue?: number;
    denominator?: number;
    denominatorValue?: number;
    recordedBy?: string;
    notes?: string;
  }>) => {
    const payload = items.map(item => {
      const num = item.numerator ?? item.numeratorValue ?? 0;
      const den = item.denominator ?? item.denominatorValue ?? 1;
      return {
        ...item,
        numerator: num,
        numeratorValue: num,
        denominator: den,
        denominatorValue: den,
      };
    });
    return apiRequest<IndicatorValue[]>(`/performance/indicators/${indicatorId}/values/batch`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // Set Target
  setTarget: (indicatorId: string, data: {
    targetYear: number;
    targetValue: number;
    targetDirection: 'BELOW' | 'ABOVE';
    tolerableValue?: number;
    unit: string;
    justification?: string;
    effectiveFrom: string;
    effectiveTo?: string;
  }) => {
    return apiRequest<IndicatorTarget>(`/performance/indicators/${indicatorId}/target`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Set Threshold (1σ, 2σ, 3σ)
  setThreshold: (indicatorId: string, data: {
    baselinePeriod: string;
    baselineMean: number;
    baselineStdDev: number;
    alert1Value: number;
    alert2Value: number;
    alert3Value: number;
    reviewDate?: string;
  }) => {
    return apiRequest<IndicatorThreshold>(`/performance/indicators/${indicatorId}/threshold`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Safety Review Board (SRB)
  getReviews: (organizationId?: string) => {
    const query = organizationId ? `?organizationId=${organizationId}` : '';
    return apiRequest<SafetyPerformanceReview[]>(`/performance/reviews${query}`);
  },

  createReview: (data: {
    organizationId?: string;
    reviewDate: string;
    periodReviewed: string;
    chairperson: string;
    attendees: string[];
    executiveSummary: string;
    decisionsAndActions?: string;
    signedBy?: string;
  }) => {
    return apiRequest<SafetyPerformanceReview>('/performance/reviews', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Snapshots
  getSnapshots: (organizationId?: string) => {
    const query = organizationId ? `?organizationId=${organizationId}` : '';
    return apiRequest<PerformanceSnapshot[]>(`/performance/snapshots${query}`);
  },

  createSnapshot: (data: {
    organizationId?: string;
    period: string;
    lockedBy: string;
  }) => {
    return apiRequest<PerformanceSnapshot>('/performance/snapshots', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Linked Actions & Risks (Item 4: SPI Owner can define action/risk)
  getIndicatorActions: (indicatorId: string) => {
    return apiRequest<any[]>(`/performance/indicators/${indicatorId}/actions`);
  },

  createIndicatorAction: (indicatorId: string, data: {
    title: string;
    description?: string;
    actionType?: string;
    priority?: string;
    status?: string;
    assigneeName?: string;
    department?: string;
    dueDate?: string;
  }) => {
    return apiRequest<any>(`/performance/indicators/${indicatorId}/actions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getIndicatorRisks: (indicatorId: string) => {
    return apiRequest<any[]>(`/performance/indicators/${indicatorId}/risks`);
  },

  createIndicatorRisk: (indicatorId: string, data: {
    title: string;
    description?: string;
    initialSeverity?: number;
    initialLikelihood?: string;
    ownerName?: string;
    department?: string;
    justification?: string;
  }) => {
    return apiRequest<any>(`/performance/indicators/${indicatorId}/risks`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};
